import type { JSONObject } from "@web-monorepo/product-events/EventMetadata";

// Keep these route families in sync with marketplace/src/pages when new tutor
// surfaces are added, otherwise they will silently inherit the default legacy
// theme.
const TUTOR_ROUTE_PREFIXES = [
  "/apply",
  "/community",
  "/teachers",
  "/tutor/account-settings",
  "/tutor/apply",
  "/tutor/course",
  "/tutor/tutor-set-pricing-onboarding",
  "/tutor/price-settings",
  "/tutor/section",
  "/tutor/session",
  "/teachers/profile",
] as const;

// Keep these route families in sync with marketplace/src/pages when new
// parent-facing surfaces are added, otherwise they will silently inherit the
// default legacy theme.
const PARENT_ROUTE_PREFIXES = [
  "/additional-single-class",
  // Legacy links still mount the app shell before redirecting to search.
  "/book-more-classes",
  "/checkout",
  "/confirm-details",
  "/course",
  "/direct-booking-subscription",
  "/forgot-password",
  "/groupclass",
  "/login",
  "/parents",
  "/referrals",
  "/resume-funnel",
  "/search",
  "/searchlp1",
  "/searchlp2",
  "/searchlp3",
  "/single-class",
  "/subscription",
] as const;

export type MarketplaceTutorThemeSurface = "none" | "homepage" | "parent_app" | "tutor_app";
export type MarketplaceTutorThemeUserType = "parent" | "tutor" | null;

export interface MarketplaceTutorThemeState {
  analyticsMetadata: JSONObject;
  brand: "classdojo" | "tutor";
  isTutorTheme: boolean;
  surface: MarketplaceTutorThemeSurface;
}

interface MarketplaceTutorThemeStateArgs {
  forceTutorTheme?: boolean;
  pathname: string;
  userType: MarketplaceTutorThemeUserType;
}

function normalizePathname(pathname: string) {
  if (!pathname) return "/";
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function matchesRoutePrefix(pathname: string, routePrefix: string) {
  return pathname === routePrefix || pathname.startsWith(`${routePrefix}/`);
}

export function getMarketplaceTutorThemeUserType(roles?: string[]): MarketplaceTutorThemeUserType {
  if (roles == null || roles.length === 0) return null;
  return roles.includes("teacher") ? "tutor" : "parent";
}

export function getMarketplaceTutorThemeSurface({
  pathname: rawPathname,
  userType,
}: {
  pathname: string;
  userType: MarketplaceTutorThemeUserType;
}): MarketplaceTutorThemeSurface {
  const pathname = normalizePathname(rawPathname);

  // `/tutor/profile` is the logged-in profile editor, while `/tutor/profile/:tutorId`
  // is the public PDP shown in parent-facing flows.
  if (pathname === "/tutor/profile") return "tutor_app";
  if (matchesRoutePrefix(pathname, "/tutor/profile")) return "parent_app";

  for (const routePrefix of TUTOR_ROUTE_PREFIXES) {
    if (matchesRoutePrefix(pathname, routePrefix)) return "tutor_app";
  }

  if (pathname === "/" && userType == null) return "homepage";
  if (userType === "tutor") return "none";

  for (const routePrefix of PARENT_ROUTE_PREFIXES) {
    if (matchesRoutePrefix(pathname, routePrefix)) return "parent_app";
  }

  return "none";
}

export function getMarketplaceTutorThemeState({
  pathname,
  userType,
  forceTutorTheme = false,
}: MarketplaceTutorThemeStateArgs): MarketplaceTutorThemeState {
  const surface = getMarketplaceTutorThemeSurface({ pathname, userType });
  const isTutorTheme = forceTutorTheme || surface === "homepage" || surface === "parent_app" || surface === "tutor_app";
  const brand = isTutorTheme ? "tutor" : "classdojo";

  return {
    analyticsMetadata: {
      marketplaceTutorThemeBrand: brand,
      marketplaceTutorThemeEnabled: isTutorTheme,
      marketplaceTutorThemeSurface: surface,
    },
    brand,
    isTutorTheme,
    surface,
  };
}
